import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";

import { Info, Error, CheckCircle, Refresh } from "@material-ui/icons";

import Title from "../ui/Title";
import FormSection from "../ui/forms/input/FormSection";

import { getErrorMessageString } from "../utils/errors";
import { getArrLabel, intlFormatMessage } from "../utils/utils";

import { STATS, TASK_ERRORS, TASK_RESULTS } from "../utils/settingsConstants";
import { settings } from "../messages/settings";
import { common } from "../messages";

import { format, parseISO } from "date-fns";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = parseISO(isoString);
  return format(date, "dd/MM/yyyy HH:mm:ss");
}

function initDistrValues() {
  return { listId: "" };
}

export default function MessageDistribution({
  bmapi,
  messageId,
  intl,
  startLoading,
  stopLoading,
  notifySuccess,
  notifyError,
}) {
  const [distrValues, setDistrValues] = useState(initDistrValues());
  const [targets, setTargets] = useState([]);
  const [runningTasks, setRunningTasks] = useState([]);
  const [pastTasks, setPastTasks] = useState([]);
  const [taskId, setTaskId] = useState(null);
  const [taskStats, setTaskStats] = useState([]);
  const [taskErrors, setTaskErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // ---- targets ----
  useEffect(() => {
    if (!bmapi) return;

    bmapi
      .getMessageTargets()
      .then((t) =>
        setTargets((t || []).sort((a, b) => a.name.localeCompare(b.name)))
      )
      .catch((e) => notifyError(getErrorMessageString(e, intl)));
  }, [bmapi, notifyError, intl]);

  // ---- tasks ----
  const loadTasks = useCallback(() => {
    if (!messageId) return;
    if (!bmapi) return;

    startLoading();
    bmapi
      .getTasks({ processRef: messageId })
      .then((resp) => {
        const arr = resp || [];

        // SUCCESS / ERROR => "past"
        const past = [];
        const running = [];

        for (const t of arr) {
          if (t?.status === "SUCCESS" || t?.status === "ERROR") {
            past.push(t);
          } else {
            running.push(t);
          }
        }

        setPastTasks(past);
        setRunningTasks(running);
      })
      .catch((e) => notifyError(getErrorMessageString(e, intl)))
      .finally(() => stopLoading());
  }, [bmapi, messageId, startLoading, stopLoading, notifyError, intl]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // ---- handlers ----
  const handleDistrChange = useCallback((label) => {
    return (e) => {
      const val = label === "listId" ? e.target.value : e;
      setDistrValues((v) => ({ ...v, [label]: val }));
    };
  }, []);

  const handleSubmitDistr = useCallback(
    (action) => {
      if (!messageId) return;

      if (!distrValues.listId) {
        notifyError("Specificare la lista di distribuzione");
        return;
      }

      const selectedTarget = targets.find((t) => t.id === distrValues.listId);
      const is_crm = !!selectedTarget?.is_crm;
      const one_step = action === "send";
      const users_persisted = true;

      startLoading();

      bmapi
        .startDistribution(
          messageId,
          distrValues.listId,
          is_crm,
          one_step,
          users_persisted
        )
        .then(() => {
          notifySuccess(
            action === "start"
              ? "Distribuzione avviata"
              : "Distribuzione inviata"
          );
          loadTasks();
        })
        .catch((err) => notifyError(getErrorMessageString(err, intl)))
        .finally(() => stopLoading());
    },
    [
      bmapi,
      messageId,
      distrValues.listId,
      targets,
      startLoading,
      stopLoading,
      notifySuccess,
      notifyError,
      intl,
      loadTasks,
    ]
  );

  const showTask = useCallback(
    (id) => {
      if (!bmapi) return;

      setTaskId(id);
      startLoading();
      bmapi
        .getTaskStats({ task: id })
        .then((resp) => setTaskStats(resp || []))
        .catch((e) => notifyError(getErrorMessageString(e, intl)))
        .finally(() => stopLoading());
    },
    [bmapi, startLoading, stopLoading, notifyError, intl]
  );

  const getErrors = useCallback(() => {
    setShowErrors((s) => !s);

    if (!bmapi) return;
    if (taskErrors.length !== 0) return;
    if (!taskId) return;

    startLoading();
    bmapi
      .getTaskErrors({ task: taskId })
      .then((resp) => setTaskErrors(resp || []))
      .catch((e) => notifyError(getErrorMessageString(e, intl)))
      .finally(() => stopLoading());
  }, [
    bmapi,
    taskErrors.length,
    taskId,
    startLoading,
    stopLoading,
    notifyError,
    intl,
  ]);

  const closeTaskDialog = useCallback(() => {
    setOpen(false);
    setTaskStats([]);
    setTaskId(null);
    setTaskErrors([]);
  }, []);

  const getStatus = useCallback(
    (status, taskDescrId) => {
      return (
        <Box display="flex" alignItems="center">
          {status === "RUNNING" && (
            <IconButton
              size="small"
              onClick={() => {
                showTask(taskDescrId);
                setOpen(true);
              }}
            >
              <Info />
            </IconButton>
          )}
          {status === "EXECUTED-W-ERRORS" && (
            <IconButton
              size="small"
              onClick={() => {
                showTask(taskDescrId);
                setOpen(true);
              }}
            >
              <Error />
            </IconButton>
          )}
          {status === "SUCCESSFULLY-EXECUTED" && (
            <IconButton
              size="small"
              onClick={() => {
                showTask(taskDescrId);
                setOpen(true);
              }}
            >
              <CheckCircle />
            </IconButton>
          )}
          {status}
        </Box>
      );
    },
    [showTask]
  );

  function RunningTasks({ arr }) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell component="th">
                {intlFormatMessage(settings.dateTimeError, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.status, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.queueStatus, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.targetCount, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.errorsCount, "", intl)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arr.map((element, i) => (
              <TableRow key={"row" + i}>
                <TableCell component="td">
                  {formatTime(element.created_at)}
                </TableCell>
                <TableCell component="td">
                  {getStatus(element.status, element.id)}
                </TableCell>
                <TableCell component="td">{element.queue_status}</TableCell>
                <TableCell component="td">{element.items_count}</TableCell>
                <TableCell component="td">{element.errors_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  function PastTasks({ arr }) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell component="th">
                {intlFormatMessage(settings.dateTimeError, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.status, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.queueStatus, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.targetCount, "", intl)}
              </TableCell>
              <TableCell component="th">
                {intlFormatMessage(settings.errorsCount, "", intl)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arr.map((element, i) => (
              <TableRow key={"row" + i}>
                <TableCell component="td">
                  {formatTime(element.created_at)}
                </TableCell>
                <TableCell component="td">
                  {getStatus(element.status, element.id)}
                </TableCell>
                <TableCell component="td">{element.queue_status}</TableCell>
                <TableCell component="td">{element.items_count}</TableCell>
                <TableCell component="td">{element.errors_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      {/* DISTRIBUZIONE */}
      <Container maxWidth="sm" className="tablistNone">
        <Title>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Box>Distribuzione</Box>
          </Box>
        </Title>

        <form onSubmit={(e) => e.preventDefault()}>
          <FormSection>
            <TextField
              select
              label="Lista di distribuzione"
              value={distrValues.listId}
              onChange={handleDistrChange("listId")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            >
              {targets?.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}

              {!targets?.length && (
                <MenuItem value="" disabled>
                  Nessun target disponibile
                </MenuItem>
              )}
            </TextField>
          </FormSection>

          {bmapi.settings.messageDistributionTwoSteps ? (
            <Button
              variant="contained"
              fullWidth
              color="primary"
              type="button"
              disabled={!distrValues.listId}
              onClick={() => handleSubmitDistr("start")}
            >
              Avvia distribuzione
            </Button>
          ) : (
            <Button
              variant="contained"
              fullWidth
              color="primary"
              type="button"
              disabled={!distrValues.listId}
              onClick={() => handleSubmitDistr("send")}
            >
              Invia Distribuzione
            </Button>
          )}
        </form>
      </Container>

      {/* DISTRIBUZIONI IN CORSO */}
      {runningTasks.length !== 0 && (
        <Container maxWidth="sm" className="tablistNone">
          <Title>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Box>Distribuzioni in corso</Box>
              <IconButton size="small" onClick={loadTasks}>
                <Refresh />
              </IconButton>
            </Box>
          </Title>
          <RunningTasks
            arr={runningTasks}
            constArr={TASK_RESULTS}
            useId={true}
          />
        </Container>
      )}

      {/* DISTRIBUZIONI EFFETTUATE */}
      {pastTasks.length !== 0 && (
        <Container maxWidth="sm" className="tablistNone">
          <Title>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Box>Distribuzioni effettuate</Box>
              <IconButton size="small" onClick={loadTasks}>
                <Refresh />
              </IconButton>
            </Box>
          </Title>
          <PastTasks arr={pastTasks} constArr={TASK_RESULTS} useId={true} />
        </Container>
      )}

      {/* POP UP DETTAGLI TASK */}
      {taskStats.length !== 0 && (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={closeTaskDialog}>
          <DialogTitle>Dettagli distribuzione</DialogTitle>
          <DialogContent>
            <Box>
              <Typography style={{ fontWeight: "bold" }}>
                ID: {taskId}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableBody>
                  {Object.entries(taskStats).map((t) => (
                    <TableRow key={t[0]}>
                      <TableCell
                        component="th"
                        scope="row"
                        style={{ fontWeight: 500 }}
                      >
                        {getArrLabel(STATS, t[0])}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {t[1]}
                      </TableCell>
                      <TableCell align="right">
                        {t[0] === "Errors" && t[1] > 0 && (
                          <Button onClick={getErrors} variant="contained">
                            {intl.formatMessage(settings.showErrors)}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeTaskDialog} color="primary">
              {intl.formatMessage(common.close)}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* POP UP ERRORI */}
      {showErrors && (
        <Dialog
          fullWidth
          maxWidth="sm"
          open={showErrors}
          onClose={() => setShowErrors(false)}
        >
          <DialogTitle>{intl.formatMessage(settings.executing)}</DialogTitle>
          <DialogContent>
            <PastTasks arr={taskErrors} constArr={TASK_ERRORS} useId={false} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowErrors(false)} color="primary">
              {intl.formatMessage(common.close)}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
